import { useTheme } from '@/src/context/ThemeContext';
import { UseCaseLandingConfig, UseCaseLandingScreen } from '@/src/presentation/screens/UseCaseLandingScreen';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

const PAGE_URL = 'https://tournaly.app/king-of-the-hill-tournament';
const OG_IMAGE = 'https://tournaly.app/og-image.png';

const CONFIG: UseCaseLandingConfig = {
  badge: 'King of the Hill · Pro',
  badgeEs: 'Rey de la Colina · Pro',
  badgeIcon: 'ribbon-outline',
  title: 'King of the Hill',
  titleEs: 'Torneos',
  titleAccent: 'Tournament Maker',
  titleAccentEs: 'Rey de la Colina',
  subtitle:
    'Defend the throne. Challengers face the reigning king one by one — the player who survives all challenges wins the crown.',
  subtitleEs:
    'Defiende el trono. Los retadores se enfrentan al rey reinante uno a uno — el jugador que sobrevive a todos los desafíos gana la corona.',
  isPro: true,
  steps: [
    {
      title: 'Set the first king',
      titleEs: 'Elige al primer rey',
      desc: 'The first match decides who starts on the throne. The winner becomes king.',
      descEs: 'El primer partido decide quién empieza en el trono. El ganador se convierte en rey.',
    },
    {
      title: 'Challengers queue up',
      titleEs: 'Los retadores hacen cola',
      desc: 'Remaining participants challenge the king one by one in order. Each match has one clear outcome.',
      descEs: 'Los participantes restantes retan al rey uno a uno en orden. Cada partido tiene un resultado claro.',
    },
    {
      title: 'Last king standing wins',
      titleEs: 'El último rey en pie gana',
      desc: 'After all challengers have competed, the reigning king is the tournament champion.',
      descEs: 'Tras todos los desafíos, el rey reinante es el campeón del torneo.',
    },
  ],
  features: [
    {
      icon: 'ribbon-outline',
      color: '#FFD700',
      title: 'Automatic King Tracking',
      titleEs: 'Seguimiento Automático del Rey',
      desc: 'The current king is always shown. When a challenger wins, they automatically become the new king.',
      descEs: 'El rey actual siempre está visible. Cuando un retador gana, automáticamente se convierte en el nuevo rey.',
    },
    {
      icon: 'people-circle-outline',
      color: '#FF6B35',
      title: 'Challenger Queue',
      titleEs: 'Cola de Retadores',
      desc: 'Participants are queued in order — everyone knows who is next to challenge the throne.',
      descEs: 'Los participantes están en cola en orden — todos saben quién reta al trono a continuación.',
    },
    {
      icon: 'flash-outline',
      color: '#4ECDC4',
      title: 'Continuous Play Format',
      titleEs: 'Formato de Juego Continuo',
      desc: 'No downtime between matches — the format keeps the competition flowing without breaks.',
      descEs: 'Sin tiempo muerto entre partidos — el formato mantiene la competición en marcha sin interrupciones.',
    },
    {
      icon: 'trophy-outline',
      color: '#10B981',
      title: 'Perfect for FGs & Gaming',
      titleEs: 'Perfecto para Videojuegos y Juegos de Lucha',
      desc: 'King of the Hill is the go-to format for fighting games, pool, darts and casual gaming sessions.',
      descEs: 'Rey de la Colina es el formato ideal para juegos de lucha, billar, dardos y sesiones de juego casual.',
    },
    {
      icon: 'share-social-outline',
      color: '#06B6D4',
      title: 'Export & Share',
      titleEs: 'Exportar y Compartir',
      desc: 'Share the bracket and results as a PNG or export a PDF of the full tournament record.',
      descEs: 'Comparte el bracket y resultados como PNG o exporta un PDF del registro completo del torneo.',
    },
    {
      icon: 'phone-portrait-outline',
      color: '#A78BFA',
      title: 'Mobile Apps',
      titleEs: 'Apps Móviles',
      desc: 'Manage your King of the Hill tournament live from the iOS or Android app.',
      descEs: 'Gestiona tu torneo Rey de la Colina en directo desde la app de iOS o Android.',
    },
  ],
  relatedBlogSlug: '2026-03-03-king-of-the-hill',
  relatedBlogTitle: 'King of the Hill Tournament Format — How It Works',
  relatedBlogTitleEs: 'Formato Rey de la Colina — Cómo Funciona',
  relatedBlogSummary: 'A complete breakdown of the King of the Hill format: rules, how challengers queue, and why it keeps every match exciting.',
  relatedBlogSummaryEs: 'Un análisis completo del formato Rey de la Colina: reglas, cola de retadores y por qué mantiene cada partido emocionante.',
};

const title = 'King of the Hill Tournament Maker — Free Online Tool | Tournaly';
const description =
  'Run King of the Hill tournaments online. Automatic king tracking, challenger queue, continuous play format. Included in Tournaly Pro.';

export default function KingOfTheHillTournamentPage() {
  const { colors, isDark, toggleTheme } = useTheme();
  const router = useRouter();

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Tournaly — King of the Hill Tournament Maker',
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web, iOS, Android',
    url: PAGE_URL,
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Tournaly', url: 'https://tournaly.app' },
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Tournaly" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="keywords" content="king of the hill tournament, king of the hill format, challenger tournament, throne tournament, gaming tournament maker" />
        <script type="application/ld+json">{jsonLd}</script>
      </Head>
      <UseCaseLandingScreen
        config={CONFIG}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onGetStarted={() => router.push('/auth/register' as any)}
        onSignIn={() => router.push('/auth/login' as any)}
        onBlogPostPress={(slug) => {
          if (slug === 'blog') router.push('/blog' as any);
          else if (slug === 'privacy') router.push('/privacy' as any);
          else if (slug === 'terms') router.push('/terms' as any);
          else router.push(`/blog/${slug}` as any);
        }}
        colors={colors}
      />
    </>
  );
}
